import React, { useState } from "react";
import { Box, Button, FormControlLabel, Paper, Radio, RadioGroup, Typography } from "@mui/material";

interface IQueryMessageProps {
    title: string;
    message: string;
    color: "warning" | "success";
    subdued?: boolean;
    children?: React.ReactNode;
}

export const QueryMessage: React.FC<IQueryMessageProps> = ({ title, message, color, subdued, children }) => (
    <Paper
        variant="outlined"
        role="status"
        sx={{
            mb: subdued ? 2 : 3,
            p: subdued ? 1.5 : 2.5,
            borderLeft: 6,
            borderLeftColor: `${color}.main`,
            backgroundColor: subdued ? "transparent" : color === "warning" ? "#FFF9DB" : "#EDF7ED",
            width: "100%",
            boxSizing: "border-box"
        }}
    >
        {!subdued && <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>{title}</Typography>}
        {subdued
            ? <Typography variant="body2" color="text.secondary">{message}</Typography>
            : <Typography variant="body1" sx={{ fontSize: "1.125rem", lineHeight: 1.6 }}>{message}</Typography>}
        {children}
    </Paper>
);

const NONE_OF_THE_ABOVE = "none-of-the-above";

// Appends the chosen clarification to the existing text, ending the existing text with a period first.
export const appendClarificationChoice = (existing: string, choice: string): string => {
    const trimmed = existing.trimEnd();
    if (trimmed.length === 0)
        return choice;

    const sentence = trimmed.endsWith(".") ? trimmed : `${trimmed}.`;
    return `${sentence} ${choice}`;
};

interface IClarificationChoicesProps {
    choices: string[];
    disabled?: boolean;
    // called with the chosen text, or null when "None of the above" was chosen
    onUpdateRequest: (choice: string | null) => void;
}

export const ClarificationChoices: React.FC<IClarificationChoicesProps> = ({ choices, disabled, onUpdateRequest }) => {
    const [selected, setSelected] = useState<string>("");

    if (choices.length === 0)
        return null;

    return (
        <Box sx={{ mt: 2 }}>
            <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
                {choices.map((choice) => (
                    <FormControlLabel key={choice} value={choice} control={<Radio disabled={disabled} />} label={choice} />
                ))}
                <FormControlLabel value={NONE_OF_THE_ABOVE} control={<Radio disabled={disabled} />} label="None of the above" />
            </RadioGroup>
            <Button
                variant="contained"
                sx={{ mt: 1 }}
                disabled={!selected || disabled}
                onClick={() => onUpdateRequest(selected === NONE_OF_THE_ABOVE ? null : selected)}
            >
                Update request
            </Button>
        </Box>
    );
};
